import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthServiceV2, RefreshTokenRequestDtoV2 } from 'api-client';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { ImpersonationStore } from 'shared';

import { TokenStorageService } from './token-storage.service';
import { AuthStore } from '../store/auth.store';

let isRefreshing = false;
const refreshSubject$ = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const tokenStorage = inject(TokenStorageService);
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const impersonationStore = inject(ImpersonationStore);
  const authService = inject(AuthServiceV2);

  if (typeof window !== 'undefined' && sessionStorage.getItem('logout_in_progress')) {
    return next(req);
  }

  const url = req.url.toLowerCase();
  const token = tokenStorage.getToken();

  // Endpoints that require JWT authentication (V1 and V2)
  const requiresJwt =
    url.includes('/api/auth/') ||
    url.includes('/api/v2/auth/') ||
    url.includes('/api/userestablishments') ||
    url.includes('/api/v2/userestablishments') ||
    url.includes('/api/users') ||
    url.includes('/api/v2/users') ||
    url.includes('/api/v2/establishments') ||
    url.includes('/api/v2/transactions') ||
    url.includes('/api/v2/pos') ||
    url.includes('/api/v2/paymentlinks') ||
    url.includes('/api/v2/upload') ||
    url.includes('/api/v2/documents');

  // Public auth endpoints that don't need JWT (V1 and V2)
  const isPublicAuthEndpoint =
    url.includes('/api/auth/login') ||
    url.includes('/api/v2/auth/login') ||
    url.includes('/api/auth/register') ||
    url.includes('/api/v2/auth/register') ||
    url.includes('/api/auth/refresh-token') ||
    url.includes('/api/v2/auth/refresh-token') ||
    url.includes('/api/auth/send-password-reset') ||
    url.includes('/api/v2/auth/send-password-reset') ||
    url.includes('/api/auth/reset-password') ||
    url.includes('/api/v2/auth/reset-password') ||
    url.includes('/api/auth/verify-email') ||
    url.includes('/api/v2/auth/verify-email') ||
    url.includes('/api/auth/send-verification') ||
    url.includes('/api/v2/auth/send-verification');

  // Add token for JWT-protected endpoints (except public ones)
  if (requiresJwt && !isPublicAuthEndpoint && token?.accessToken) {
    const authReq = addTokenToRequest(req, token.accessToken);
    return next(authReq).pipe(
      catchError((error: unknown) => {
        if (isHttpErrorResponse(error)) {
          if (error.status === 401) {
            return handleTokenRefresh(
              req,
              next,
              tokenStorage,
              authStore,
              authService,
              router,
              impersonationStore,
            );
          }
          if (error.status === 403) {
            void router.navigate(['/auth/access-denied']);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  // All other requests go through without JWT (they use ApiKey)
  return next(req);
};

function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokenStorage: TokenStorageService,
  authStore: AuthStore,
  authService: AuthServiceV2,
  router: Router,
  impersonationStore: ImpersonationStore,
): Observable<HttpEvent<unknown>> {
  const token = tokenStorage.getToken();
  if (!token?.refreshToken) {
    impersonationStore.clearStorage();
    authStore.logout();
    void router.navigate(['/auth/session-expired']);
    return throwError(() => new Error('No refresh token available'));
  }

  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject$.next(null);

    const refreshRequest: RefreshTokenRequestDtoV2 = {
      refreshToken: token.refreshToken,
    };

    return authService.apiV2AuthRefreshTokenPost(refreshRequest).pipe(
      switchMap((response) => {
        isRefreshing = false;
        const newToken = response?.data;
        if (newToken?.accessToken) {
          tokenStorage.saveToken(newToken, tokenStorage.isRememberMeEnabled());
          refreshSubject$.next(newToken.accessToken);
          return next(addTokenToRequest(req, newToken.accessToken));
        }
        refreshSubject$.next(null);
        impersonationStore.clearStorage();
        authStore.logout();
        void router.navigate(['/auth/session-expired']);
        return throwError(() => new Error('Invalid refresh response'));
      }),
      catchError((refreshError: unknown) => {
        isRefreshing = false;
        refreshSubject$.next(null);
        impersonationStore.clearStorage();
        authStore.logout();
        void router.navigate(['/auth/session-expired']);
        return throwError(() => refreshError);
      }),
    );
  } else {
    return refreshSubject$.pipe(
      filter((newToken): newToken is string => newToken !== null),
      take(1),
      switchMap((newToken) => next(addTokenToRequest(req, newToken))),
    );
  }
}

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse;
}

function addTokenToRequest(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
